import {
  SessionEvidenceType,
  ValidationSeverity,
  type MessageEvent,
  type SessionEvidence,
  type SimulationRun,
  type TestCase,
  type TestCaseVerdict,
  type TestSession,
  type ValidationResult,
} from '@k-ucap/shared';

/**
 * Prompt 14 Phase 3: Evidence Chain
 * PASS는 저장하지 않고 FAIL / WARN / 중요 이벤트만 Evidence로 기록한다.
 */

export interface CreateEvidenceInput {
  sessionId: string;
  runId?: string;
  messageEventId?: string;
  messageId?: string;
  testCaseId?: string;
  type: SessionEvidenceType;
  title: string;
  description?: string;
  severity: string;
  expected?: unknown;
  actual?: unknown;
  source?: string;
  data?: Record<string, unknown>;
  createdBy: string;
  timestamp?: string;
}

const shouldPersistSeverity = (severity: string): boolean =>
  severity === ValidationSeverity.FAIL || severity === ValidationSeverity.WARN;

const classifyIssueType = (issueCode: string, path: string): SessionEvidenceType => {
  const code = issueCode.toUpperCase();
  if (code.includes('ROLE')) return SessionEvidenceType.ROLE_ERROR;
  if (code.includes('SEQUENCE') || code.includes('ORDER')) return SessionEvidenceType.SEQUENCE_ERROR;
  if (code.includes('TIMING') || code.includes('PERIOD') || code.includes('INTERVAL')) {
    return SessionEvidenceType.TIMING_ERROR;
  }
  if (code.includes('COORD') || path.toLowerCase().includes('lat') || path.toLowerCase().includes('lon')) {
    return SessionEvidenceType.GIS_POSITION;
  }
  return SessionEvidenceType.FIELD_ERROR;
};

export const EvidenceService = {
  /**
   * Validation 결과에서 Evidence 항목 생성. PASS이면 null 반환 (저장 안 함).
   */
  createFromValidation(
    session: TestSession,
    event: MessageEvent,
    result: ValidationResult,
    run?: SimulationRun | null
  ): Array<Omit<SessionEvidence, 'id' | 'createdAt'>> {
    if (!shouldPersistSeverity(result.severity)) return [];
    const items: Array<Omit<SessionEvidence, 'id' | 'createdAt'>> = [];
    const issues = result.issues.filter(
      (i) => i.severity === ValidationSeverity.FAIL || i.severity === ValidationSeverity.WARN
    );
    if (issues.length === 0) {
      items.push({
        sessionId: session.id,
        runId: run?.id ?? event.runId,
        messageEventId: event.id,
        messageId: event.id,
        type: SessionEvidenceType.VALIDATION,
        timestamp: event.timestamp,
        title: `#${event.messageType} ${event.name} 검증 ${result.severity}`,
        description: 'Validation 이슈 상세 없음',
        severity: result.severity,
        source: 'validation',
        data: { messageType: event.messageType },
        createdBy: 'evidence-service',
      });
      return items;
    }
    for (const issue of issues) {
      const extra = issue as unknown as Record<string, unknown>;
      items.push({
        sessionId: session.id,
        runId: run?.id ?? event.runId,
        messageEventId: event.id,
        messageId: event.id,
        type: classifyIssueType(issue.code, issue.path ?? ''),
        timestamp: event.timestamp,
        title: `#${event.messageType} ${issue.code}`,
        description: issue.message,
        severity: issue.severity,
        expected: extra.expected,
        actual: extra.actual,
        source: 'validation',
        data: {
          messageType: event.messageType,
          path: issue.path,
          code: issue.code,
        },
        createdBy: 'evidence-service',
      });
    }
    return items;
  },

  /**
   * Test Case Verdict 결과에서 Evidence 생성. PASS이면 빈 배열.
   */
  createFromVerdict(
    session: TestSession,
    testCase: TestCase,
    verdict: TestCaseVerdict,
    runId?: string
  ): Array<Omit<SessionEvidence, 'id' | 'createdAt'>> {
    if (verdict.status === 'PASS') return [];
    const items: Array<Omit<SessionEvidence, 'id' | 'createdAt'>> = [];
    const evidences = verdict.evidence.length > 0 ? verdict.evidence : [{}];
    for (const evidence of evidences) {
      items.push({
        sessionId: session.id,
        runId,
        messageEventId: evidence.messageId,
        messageId: evidence.messageId,
        testCaseId: testCase.id,
        type: SessionEvidenceType.VALIDATION,
        timestamp: evidence.timestamp ?? new Date().toISOString(),
        title: `${testCase.name} 판정 ${verdict.status}`,
        description: evidence.issue ?? `Test case verdict: ${verdict.status}`,
        severity: verdict.status === 'FAIL' ? 'FAIL' : 'WARN',
        expected: evidence.expected,
        actual: evidence.actual,
        source: 'verdict',
        data: { messageType: evidence.messageType, field: evidence.field },
        createdBy: 'evidence-service',
      });
    }
    return items;
  },

  summarize(evidence: SessionEvidence[]): { pass: number; warn: number; fail: number; total: number } {
    let warn = 0;
    let fail = 0;
    let pass = 0;
    for (const item of evidence) {
      if (item.severity === 'FAIL') fail++;
      else if (item.severity === 'WARN') warn++;
      else pass++;
    }
    return { pass, warn, fail, total: evidence.length };
  },
};

export default EvidenceService;
